import { Globe2, Lock, Lightbulb, Sprout } from "lucide-react";
import { HandleAvatar } from "@/components/HandleAvatar";
import { CATEGORY_META } from "@/components/community/meta";

function Toggle({ testid, on, onClick, onLabel, offLabel, onCls }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`px-4 py-2 rounded-full text-xs font-semibold transition ${on ? onCls : "bg-stone-200 text-stone-700 hover:bg-stone-300"}`}>
      {on ? onLabel : offLabel}
    </button>
  );
}

function HandleRow({ handle, onChange }) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap rounded-2xl bg-stone-50 px-4 py-3" data-testid="handle-row">
      <div className="flex items-center gap-3 text-sm">
        <HandleAvatar handle={handle || ""} size="lg" />
        <div>
          <span className="text-xs uppercase tracking-widest text-stone-500 font-bold">Your handle</span>
          <div className="font-display font-bold text-stone-900 text-base" data-testid="my-handle">{handle}</div>
          <div className="text-xs text-stone-500">Members only ever see this handle and icon — never your name.</div>
        </div>
      </div>
      <button data-testid="change-handle" onClick={onChange} className="px-4 py-2 rounded-full text-xs font-semibold bg-stone-200 text-stone-700 hover:bg-stone-300 transition">Change handle</button>
    </div>
  );
}

function PrefRow({ icon: Icon, iconCls, title, children, toggle }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 ${iconCls}`} />
        <div>
          <div className="font-semibold text-stone-900 text-sm">{title}</div>
          <p className="text-xs text-stone-500 mt-0.5 max-w-md">{children}</p>
        </div>
      </div>
      {toggle}
    </div>
  );
}

export function SharingSettings({ me, categories, onToggleShare, onToggleCategory, onToggleSuggestions, onToggleShareHabits, onChangeHandle }) {
  return (
    <div className="warm-card p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            {me.share_progress ? <Globe2 className="w-5 h-5 text-emerald-700" /> : <Lock className="w-5 h-5 text-stone-500" />}
            <span className="font-display font-bold text-stone-900">{me.share_progress ? "You're visible to your categories" : "You're private"}</span>
          </div>
          <p className="text-sm text-stone-500 mt-1">
            {me.share_habits ? <>Your <em>level</em> and your habit names are shared — never notes, moods or check-ins.</> : <>Only your <em>level</em> (e.g. "Silver in Exercise") is shared — never habit titles or notes.</>}
          </p>
        </div>
        <button data-testid="toggle-share" onClick={onToggleShare}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition ${me.share_progress ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-stone-200 text-stone-700 hover:bg-stone-300"}`}>
          {me.share_progress ? "Sharing on" : "Turn on sharing"}
        </button>
      </div>

      {me.share_progress && (
        <div className="mt-5 pt-5 border-t border-stone-100 space-y-5">
          <HandleRow handle={me.community_handle} onChange={onChangeHandle} />
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-3">Categories you're part of</div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const on = me.joined_categories.includes(c.category);
                const meta = CATEGORY_META[c.category];
                return (
                  <button key={c.category} data-testid={`category-toggle-${c.category}`} onClick={() => onToggleCategory(c.category)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${on ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700"}`}>
                    {meta.emoji} {meta.label}
                  </button>
                );
              })}
            </div>
          </div>
          <PrefRow icon={Lightbulb} iconCls="text-amber-700" title="Accept suggestions from members"
            toggle={<Toggle testid="toggle-suggestions" on={me.accept_suggestions} onClick={onToggleSuggestions} onLabel="Suggestions on" offLabel="Support only" onCls="bg-amber-600 text-white hover:bg-amber-700" />}>
            Everyone can send you support. Only if this is on can members offer ideas or advice. Off by default.
          </PrefRow>
          <PrefRow icon={Sprout} iconCls="text-emerald-700" title="Share my actual habits"
            toggle={<Toggle testid="toggle-share-habits" on={me.share_habits} onClick={onToggleShareHabits} onLabel="Habits visible" offLabel="Habits hidden" onCls="bg-emerald-700 text-white hover:bg-emerald-800" />}>
            Show the names of your habits (e.g. "Evening walk", "No phone in bed") to members in your categories — never your notes, moods or check-ins.
            {me.accept_suggestions && " Since you accept suggestions, this helps members offer ideas that actually fit."}
          </PrefRow>
        </div>
      )}
    </div>
  );
}
